/**
 * crm-plugin.ts  (v2)
 *
 * CrmPlugin — MeshPlugin implementation.
 * Registered under namespace 'crm'.
 * Receives CollabEnvelopes, parses, checks RBAC, and dispatches to InMemoryCrmStore.
 */

import type { CollabEnvelope } from '../../types/envelope.js';
import type { MeshPlugin } from '../../types/plugin.js';
import { InMemoryCrmStore } from './crm-store.js';
import { CrmPermissions } from './crm-permissions.js';
import { mountCrmStore } from './crm-adapter.js';
import type { CrmCommand, CrmCommandType, CrmPrincipal } from './crm-contracts.js';
import { generateUUIDv7 } from '../../crypto/uuidv7.js';

const VALID_COMMAND_TYPES = new Set<CrmCommandType>([
  'ADD_CONTACT', 'UPDATE_CONTACT', 'ARCHIVE_CONTACT', 'MERGE_CONTACTS',
  'ADD_ORGANIZATION', 'UPDATE_ORGANIZATION', 'ARCHIVE_ORGANIZATION',
  'ADD_DEAL', 'UPDATE_DEAL', 'MOVE_DEAL_STAGE', 'ARCHIVE_DEAL',
  'ADD_ACTIVITY', 'COMPLETE_ACTIVITY',
]);

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function parseCrmCommand(payload: unknown, senderId: string): CrmCommand | null {
  if (!isRecord(payload)) return null;

  const { commandId, actorId, expectedRevision, type, action, payload: innerPayload } = payload;
  const cmdType = (type || action) as CrmCommandType;

  if (typeof cmdType !== 'string' || !VALID_COMMAND_TYPES.has(cmdType)) {
    return null;
  }

  return {
    commandId: typeof commandId === 'string' && commandId ? commandId : generateUUIDv7(),
    actorId: typeof actorId === 'string' ? actorId : senderId,
    expectedRevision: typeof expectedRevision === 'string' ? expectedRevision : 'rev-0',
    type: cmdType,
    payload: isRecord(innerPayload) ? innerPayload : payload,
  };
}

export class CrmPlugin implements MeshPlugin {
  readonly namespace = 'crm';

  private readonly stores = new Map<string, InMemoryCrmStore>();
  private readonly permissions = new CrmPermissions();

  getStore(roomId: string): InMemoryCrmStore {
    if (!this.stores.has(roomId)) {
      const store = new InMemoryCrmStore(roomId);
      this.stores.set(roomId, store);
      if (this.stores.size === 1) {
        mountCrmStore(store);
      }
    }
    return this.stores.get(roomId)!;
  }

  mountRoom(roomId: string): void {
    mountCrmStore(this.getStore(roomId));
  }

  onReceive(envelope: CollabEnvelope<unknown>): void {
    const match = envelope.destinationUri.match(/^hts:\/\/mesh\/crm\/(.+)$/);
    const roomId = match ? match[1] : 'general';

    const command = parseCrmCommand(envelope.payload, envelope.senderId);
    if (!command) {
      console.warn('[CrmPlugin] Received invalid payload — ignored');
      return;
    }

    const principal: CrmPrincipal = CrmPermissions.ownerPrincipal(envelope.senderId);

    if (!this.permissions.canExecute(principal, command.type as any)) {
      console.warn(`[CrmPlugin] RBAC denied: ${envelope.senderId} cannot execute ${command.type}`);
      return;
    }

    const store = this.getStore(roomId);
    const result = store.dispatch(command);

    if (!result.ok && 'reason' in result) {
      console.warn(`[CrmPlugin] Command rejected: ${result.reason}`);
    }
  }

  getState(resourceId: string): InMemoryCrmStore {
    return this.getStore(resourceId);
  }
}

export const crmPlugin = new CrmPlugin();
